// TIP-614 — the buy-extra-briefs checkout, copy and derived sentences.
// Every string is from `Checkout Flow.dc.html`, sections 2a-2d (desktop) and
// 3c (mobile). Kept out of the components for the same reason the rest of this
// feature does it: a price or a plural appears once, and a test can assert the
// sentence without rendering a modal.
// NO PRICE IS A LITERAL HERE. Every figure is passed in — from the pricing
// module for the order, and from the allowance API for the member unit price.
// The ticket is explicit about why: the membership price has already drifted
// three times (the design says AED 99, PaymentScreen hardcodes 99, and it is
// actually AED 99.90), so a fourth copy of a price in this feature is a bug
import {
  briefsCount,
  briefsInWords,
  numberWord,
} from "../../../../constants/counterpartyBriefPricing";

export const BUY_COPY = {
  // ---- Step 1 -----------------------------------------------------------
  eyebrow: "Counterparty brief",
  title: "Buy extra briefs",
  sub: "Added to your balance. Use them on any company, any time.",
  close: "Close",

  quantityLabel: "How many briefs",
  /** "AED 149.00 each" / "AED 134.10 each, member price" */
  unitNote: (unit, isMember) => (isMember ? `${unit} each, member price` : `${unit} each`),

  decrease: "One fewer brief",
  increase: "One more brief",
  /** The stepper shows the digit — "3 briefs" — while the prose spells it out. */
  quantityValue: briefsCount,

  /* "Member price. You save AED 44.70 on these three." The design writes the quantity as a word here and shows this line only for a member. One brief takes a... */
  savings: (amount, quantity) =>
    quantity === 1
      ? `Member price. You save ${amount} on this brief.`
      : `Member price. You save ${amount} on these ${numberWord(quantity)}.`,

  /** "Counterparty brief × 1" / "Counterparty briefs × 3" */
  lineLabel: (quantity) =>
    `Counterparty brief${quantity === 1 ? "" : "s"} × ${quantity}`,
  /** Drawn from the live rate: "VAT 5%" in the UAE, "VAT 0%" outside it. */
  /* TAX IS NOT SHOWN, for anybody. */
  totalLabel: "Total",
  excludingTax: "(excluding tax)",

  /* VAT before the billing address exists. */
  vatNote: "Final VAT is set by the billing address you enter at payment.",

  noExpiry:
    "Bought briefs do not expire and do not count against your monthly allowance.",

  /**
   * The standard-price upsell.
   *
   * Deliberately does NOT state the membership price. The design's sentence
   * ends "Membership is AED 99 / month", which is the wrong figure — it is
   * AED 99.90 — and the right one lives on the backend, not in a constant
   * here. TIP-615 builds the membership step and reads the price from the API;
   * until then this sells the brief price and the quota, both of which this
   * page already knows for certain.
   */
  upsell: (memberUnit) =>
    `Members pay ${memberUnit} a brief and get 30 a month with no weekly cap.`,
  seeMembership: "See membership",

  /* The endpoint has not given us a price. */
  noPrice: "We could not load today's brief price. Reload the page and try again.",

  cta: "Continue to payment",
  ctaHelper: "Card details are entered on the next step, with Stripe.",
  notNow: "Not now",

  // ---- The loading bridge, between step 1 and step 2 ---------------------
  // A card-shaped skeleton, not a spinner over a blank panel: it sets the
  // expectation that fields are about to appear, and the summary is already in
  // place so the wait is not dead time.
  bridgeTitle: "Setting up secure payment",
  bridgeSub: "This takes a second. Do not refresh.",
  bridgeNote: "Talking to Stripe",

  // ---- Step 2 ------------------------------------------------------------
  orderEyebrow: "Your order",
  /** "One counterparty brief" / "Three counterparty briefs" */
  orderTitle: (quantity) =>
    `${briefsInWords(quantity).split(" ")[0]} counterparty brief${quantity === 1 ? "" : "s"}`,
  orderSub: "Added to your balance as soon as payment clears.",
  /** The narrower left column shortens the line label. */
  summaryLineLabel: (quantity) => `Brief${quantity === 1 ? "" : "s"} × ${quantity}`,
  totalShort: "Total",
  memberApplied: (unit) => `Member price applied. ${unit} each.`,
  /* "Invoice goes to hassan.ali@yopmail.com after payment." — design 1d. */
  invoiceTo: (email) => `Invoice goes to ${email} after payment.`,
  back: "Back",

  paymentTitle: "Payment",
  paymentSub: "Stripe handles the card. We never see the number.",
  // No TREVEX pay button on this screen. Adding one beside Stripe's own
  // confirm button is the fastest way to get double-clicks and support
  // tickets, which is why the design says so twice.
  stripeFooter: "Payments are processed by Stripe. TREVEX does not store your card.",

  // ---- Could not start checkout ------------------------------------------
  // Only session creation is ours to report. Stripe's own errors happen inside
  // its iframe and are its to word.
  errorTitle: "We could not start checkout",
  // The design's sentence ends "and your plan is still selected", which is the
  // membership variant. The quantity is what survives here.
  errorBody:
    "Nothing has been charged and your order is still here. Try again, or come back in a minute.",
  tryAgain: "Try again",
  /** "Ref brief-checkout-TRX… · support@trevex.io" — the backend's own support_ref. */
  errorRef: (ref) => `Ref ${ref} · support@trevex.io`,
  supportEmail: "support@trevex.io",

  successEyebrow: "Payment received",
  /** "One brief added" / "Three briefs added" */
  successTitle: (quantity) => `${briefsInWords(quantity)} added`,
  successBody:
    "They are in your balance now. Order one whenever you need it, on any company.",
  chargedLabel: "Charged",
  referenceLabel: "Reference",
  balanceLabel: "Balance",
  balanceValue: (bought) =>
    `${bought} bought brief${bought === 1 ? "" : "s"}, no expiry`,
  orderABrief: "Order a brief",
  viewReceipt: "View receipt",
  /** The tooltip while the backend does not yet return a receipt URL. */
  receiptPending: "The receipt will be emailed to you.",

  /* Between Stripe's redirect and the credit landing. */
  creditingTitle: "Payment received",
  creditingBody: "Adding them to your balance\u2026",

  pendingTitle: "Payment received",
  pendingBody: "Your briefs will appear here within a minute.",
  backToBriefs: "Back to briefs",
};

export default BUY_COPY;
